// /src/utils/homeAreaSelfHealer.js
// Home-area creation and area-identity helpers: createHomeAreaFromBoundary (used by AccountSetup,
// MyProfile), area-code generation and collision handling, and identity derivation (MyLocations,
// RegisterLocationStep).
//
// 2026-10-03 (Roadmap #88, registration confirm-first): it no longer self-heals. The automatic
// home creation for a signed-in member with none (ensureHomeArea: the registration wizard's
// browser stash, then the visitor's cached area, then browser/IP geolocation) and the stash itself
// are retired — a member with no home chooses their own place in AccountSetup. The filename is
// kept (a rename is its own change).

import { supabaseFetch } from '../lib/supabase';
import { calculateDistance } from './geocentricQueries';

// Target size of a newly-seeded home área: the home freguesia itself + nearest neighbours.
// Michael-ratified default (2026-08-23 build brief) — do not replace with another bare cap.
const HOME_AREA_SEED_SIZE = 5;

// 2026-08-29 area-code collision fix, extended to all home-creation paths
// (docs/briefs/2026-08-29-area-code-collision-all-home-paths.md): area_code used to be derived
// deterministically from the resolved boundary's name (deriveAreaIdentityFromName, below) at every
// write path that creates or changes a home area — re-using a location a user (or the self-healer)
// had already used derives the SAME code, colliding with (user_id, area_code)'s unique constraint
// (23505). Originally fixed only in MyLocations.jsx (2026-08-29-mylocations-area-code-collision-fix.md);
// this is the one shared implementation every home-creation write path (MyLocations.jsx,
// ChangeHomeAreaStep.jsx, RegisterLocationStep.jsx, createHomeAreaFromBoundary below) now uses.
const AREA_CODE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

/**
 * A random 6-char uppercase+digit area_code candidate — matches the DB's own CHECK constraints
 * exactly (length=6, `^[A-Z0-9]+$`, confirmed live via pg_constraint), so a generated code can
 * never fail the field's own format validation. Pure — no uniqueness check; pair with
 * generateUniqueAreaCode for an actual write, or use bare for a transient/display-only value that
 * gets superseded by a properly-checked code before anything is persisted (e.g. a crosshair
 * identity box's preview, shown before the user has even confirmed a save).
 * @returns {string}
 */
export function generateRandomAreaCode() {
  return Array.from({ length: 6 }, () => AREA_CODE_CHARS[Math.floor(Math.random() * AREA_CODE_CHARS.length)]).join('');
}

/**
 * A random area_code, checked unique for this user, retry-capped — a fixed loop (never
 * unbounded), so it provably terminates.
 * @param {string} userId
 * @param {{existingCodes?: Iterable<string>}} [options] — existingCodes: the caller's own
 *   already-loaded area_code values (e.g. MyLocations' userAreas, ChangeHomeAreaStep's otherAreas
 *   plus its current home) — checked directly, no DB round-trip. Omit to have this function query
 *   user_areas for this user's codes itself.
 * @returns {Promise<string>}
 */
export async function generateUniqueAreaCode(userId, { existingCodes = null } = {}) {
  let used;
  if (existingCodes) {
    used = new Set(existingCodes);
  } else {
    const { data } = await supabaseFetch.query('user_areas', {
      select: 'area_code',
      eq: { user_id: userId }
    });
    used = new Set((data || []).map(a => a.area_code));
  }
  for (let i = 0; i < 10; i++) {
    const candidate = generateRandomAreaCode();
    if (!used.has(candidate)) return candidate;
  }
  return generateRandomAreaCode();
}

/**
 * True if a supabaseFetch error (from insert/update/rpc — all three share the same
 * {message, status} shape) is the (user_id, area_code) unique-constraint violation (23505 on
 * user_areas_user_id_area_code_key — confirmed live via pg_constraint, the only unique constraint
 * on user_areas reachable from any area_code write path). PostgREST maps 23505 to HTTP 409
 * regardless of whether it originates from a raw table INSERT or one inside an RPC function
 * (create_new_home_area), since the mapping is keyed on the underlying Postgres error code, not
 * how the SQL was structured. error.message is the raw, un-parsed response body (a JSON string),
 * checked as a substring fallback for callers whose error object lacks .status.
 * @param {{message?: string, status?: number}} error
 * @returns {boolean}
 */
export function isAreaCodeCollision(error) {
  return error?.status === 409 || (typeof error?.message === 'string' && error.message.includes('user_areas_user_id_area_code_key'));
}

/**
 * True if a supabaseFetch error is the database refusing an area whose base parish is not one of
 * its own parishes (CHECK user_areas_primary_in_boundaries, migration
 * 20260929_home_area_base_parish_in_area.sql; own-parish addendum 1 ruling 2). A CHECK violation
 * (23514) arrives as HTTP 400, so it is matched by the constraint's name, never by status.
 * @param {{message?: string}} error
 * @returns {boolean}
 */
export function isBaseParishRefusal(error) {
  return typeof error?.message === 'string' && error.message.includes('user_areas_primary_in_boundaries');
}

/**
 * Rank the freguesias get_freguesias_near_boundary returns for a home boundary by physical
 * distance and return the nearest `count` live (non-superseded) ids.
 *
 * No RPC returns per-candidate coordinates in one batched call (get_boundary_centroid returns
 * a single row for the whole input set, not one per id), so each candidate's centroid is fetched
 * individually via Promise.all — one round trip per candidate, bounded by the RPC's own radius
 * config (PT: tens of candidates at 30km, never unbounded). An earlier version of this function
 * approximated distance via each candidate's parent (municipality) centroid to avoid that
 * fan-out — verified against live staging data and rejected: it ranked three same-concelho
 * parishes ahead of the merged Tornada/Salir do Porto boundary (6.8km from São Martinho) purely
 * because Caldas da Rainha's much larger municipality has a more distant centroid than Nazaré's —
 * exactly the cross-border neighbour this brief is built around. Per-candidate distance is
 * required for a correct answer; the round-trip cost is accepted as proportionate for a one-time,
 * background, per-signup seed. Tie-break: distance, then id, both ascending. Never throws — any
 * failure yields [].
 */
async function getNearestFreguesias(homeBoundaryId, homeLat, homeLon, count) {
  try {
    const { data: nearby, error } = await supabaseFetch.rpc('get_freguesias_near_boundary', {
      p_boundary_id: homeBoundaryId,
      p_include_geojson: false
    });
    if (error || !nearby || nearby.length === 0) return [];

    const candidates = nearby.filter(f => f.id !== homeBoundaryId);
    if (candidates.length === 0) return [];

    // Drop superseded boundaries (e.g. post-CAOP-merge ids) — get_freguesias_near_boundary has
    // no supersession awareness (confirmed against its live body), so a freshly-seeded array
    // must filter it here rather than assume the RPC already excludes them.
    const candidateIds = candidates.map(c => c.id);
    const { data: statusRows } = await supabaseFetch.query('admin_boundaries', {
      select: 'id,superseded_by',
      inFilter: { id: candidateIds }
    });
    const liveIds = new Set((statusRows || []).filter(r => !r.superseded_by).map(r => r.id));
    const liveCandidates = candidates.filter(c => liveIds.has(c.id));
    if (liveCandidates.length === 0) return [];

    const centroids = new Map();
    await Promise.all(liveCandidates.map(async (c) => {
      const { data: centroid } = await supabaseFetch.rpc('get_boundary_centroid', {
        p_boundary_ids: [c.id]
      });
      if (centroid?.[0]?.lat != null) {
        centroids.set(c.id, centroid[0]);
      }
    }));

    const ranked = liveCandidates
      .filter(c => centroids.has(c.id))
      .map(c => {
        const p = centroids.get(c.id);
        return { id: c.id, distance: calculateDistance(homeLat, homeLon, p.lat, p.lon) };
      })
      .sort((a, b) => a.distance - b.distance || a.id - b.id);

    return ranked.slice(0, count).map(r => r.id);
  } catch (err) {
    console.warn('Self-healer: nearest-freguesias ranking failed, seeding home only:', err.message);
    return [];
  }
}

/**
 * Resolve a boundary's user_areas display fields (display_name, area_code) plus its centroid.
 * Shared by home creation (createHomeAreaFromBoundary) and a home change (MyProfile.jsx's
 * handleHomeBoundarySelected) so both compute identical display_name/area_code for the same
 * boundary id — a changed home and a freshly created home must not diverge on these fields.
 *
 * @param {number} boundaryId
 * @returns {Promise<{displayName: string, areaCode: string, centroid: {lat: number, lon: number, name: string} | null}>}
 */
export async function resolveHomeAreaDisplayFields(boundaryId) {
  const { data: centroid } = await supabaseFetch.rpc('get_boundary_centroid', {
    p_boundary_ids: [boundaryId]
  });
  const boundaryName = centroid?.[0]?.name || 'Home';
  const { displayName, areaCode } = deriveAreaIdentityFromName(boundaryName);
  return { displayName, areaCode, centroid: centroid?.[0] || null };
}

/**
 * deriveAreaIdentityFromName — the pure naming algorithm extracted out of
 * resolveHomeAreaDisplayFields (2026-08-26 crosshair tuning), so a caller that already has a
 * boundary's name in hand (e.g. the crosshair's own get_boundaries_by_ids seed, which returns
 * `.name` directly) can derive the same display_name/area_code without a second
 * get_boundary_centroid round-trip. resolveHomeAreaDisplayFields still owns the RPC call for
 * callers that only have a boundary id.
 * @param {string} boundaryName
 * @returns {{displayName: string, areaCode: string}}
 */
export function deriveAreaIdentityFromName(boundaryName) {
  const name = boundaryName || 'Home';
  // Same pattern as old handleLocationSetupAccept — do not change independently
  // of resolveHomeAreaDisplayFields; the two must keep producing identical output.
  const cleanName = name.replace(/[^a-zA-Z]/g, '').toUpperCase();
  const areaCode = (cleanName.substring(0, 4) + '01').padEnd(6, 'X').substring(0, 6);
  return { displayName: name, areaCode };
}

/**
 * Create a user_areas home row from a resolved boundary id.
 * Shared by fast path, slow path, and the registration wizard's stashed pick.
 *
 * Seeds boundary_ids as home + up to (HOME_AREA_SEED_SIZE - 1) nearest freguesias — see
 * getNearestFreguesias. Home is always element one and always present; if ranking underfills
 * or fails entirely, the area is seeded with whatever was found (never blocks home creation).
 *
 * @param {string} userId
 * @param {number} boundaryId
 * @param {{isAutoDetected?: boolean, boundaryIds?: number[], displayName?: string,
 *   areaCode?: string, colorHex?: string|null}} [options]
 *   boundaryIds — caller-supplied área (e.g. the registration wizard's explicit multi-select
 *   pick, already including boundaryId): used as-is, skipping getNearestFreguesias entirely.
 *   Omit to auto-seed nearest neighbours as usual.
 *   displayName/areaCode/colorHex — 2026-08-26 crosshair tuning: override the auto-derived
 *   identity (e.g. the wizard's identity-box, if the user edited it before Finish). Omit
 *   displayName/colorHex to fall back to resolveHomeAreaDisplayFields's derivation / no colour (as
 *   before this build — colour was never auto-derived). This is a one-time value at INSERT — it
 *   needs no "is custom" flag, unlike a LATER overwrite; see
 *   docs/escalations/2026-08-26-area-identity-override-persistence.md for the piece that does.
 *   2026-08-29 area-code collision fix, extended: areaCode is honoured on the FIRST insert attempt
 *   only (see the retry loop below) — the only caller that ever supplies it is AccountSetup
 *   (#88; until 3 Oct 2026, ensureHomeArea's wizard-stash branch), which by construction only ever
 *   creates a member's FIRST home area (zero
 *   prior user_areas rows for that user_id), so a collision there is structurally close to
 *   impossible today. If a future caller reuses this override for a NON-first area, re-examine
 *   whether silently discarding it on retry (in favour of a fresh random code) is still right.
 * @returns {Promise<{created: boolean, reason?: string, boundaryName?: string, boundaryId?: number}>}
 */
export async function createHomeAreaFromBoundary(userId, boundaryId, {
  isAutoDetected = true,
  boundaryIds: explicitBoundaryIds = null,
  displayName: displayNameOverride = null,
  areaCode: areaCodeOverride = null,
  colorHex = null
} = {}) {
  // Boundary name/centroid — the centroid also anchors the nearest-freguesia ranking below.
  // 2026-08-29 area-code collision fix, extended: no longer destructures areaCode from this call —
  // resolveHomeAreaDisplayFields's derivation is deterministic-on-name, the exact mechanism this
  // fix replaces; area_code now comes from generateUniqueAreaCode inside the retry loop below.
  const { displayName: derivedName, centroid } = await resolveHomeAreaDisplayFields(boundaryId);
  const boundaryName = displayNameOverride || derivedName;

  let boundaryIds;
  if (explicitBoundaryIds && explicitBoundaryIds.length > 0) {
    boundaryIds = explicitBoundaryIds;
  } else {
    let nearestIds = [];
    if (centroid?.lat != null) {
      nearestIds = await getNearestFreguesias(
        boundaryId, centroid.lat, centroid.lon, HOME_AREA_SEED_SIZE - 1
      );
    }
    boundaryIds = [boundaryId, ...nearestIds];
  }

  // 2026-08-29 is_home database enforcement (docs/briefs/2026-08-29-is-home-database-enforcement.md):
  // routes through create_home_area_if_none_exists — one atomic, advisory-locked RPC that checks
  // "does this user have a home" and writes the new row in the SAME transaction, closing the
  // check-then-write gap the previous session's client-side pre-check (a52e9fa0) could only
  // narrow, not eliminate (see docs/escalations/2026-08-29-is-home-not-database-enforced.md — this
  // migration is that escalation's resolution). The area-code retry loop is unchanged in shape —
  // still a fixed-count, provably-terminating loop — but now wraps an RPC call instead of a raw
  // insert; a residual area_code collision the RPC's own EXCEPTION handler re-raises (its
  // GET STACKED DIAGNOSTICS distinguishes it from an is_home collision, which the RPC absorbs
  // itself and never surfaces here) still triggers a retry with a fresh code exactly as before.
  const INSERT_RETRY_CAP = 3;
  let lastError = null;
  for (let attempt = 0; attempt < INSERT_RETRY_CAP; attempt++) {
    const areaCode = (attempt === 0 && areaCodeOverride) ? areaCodeOverride : await generateUniqueAreaCode(userId);

    const { data, error: rpcError } = await supabaseFetch.rpc('create_home_area_if_none_exists', {
      p_boundary_ids: boundaryIds,
      p_primary_boundary_id: boundaryId,
      p_display_name: boundaryName,
      p_area_code: areaCode,
      p_color_hex: colorHex,
      p_is_auto_detected: isAutoDetected
    });

    if (!rpcError) {
      if (data?.was_created) {
        console.log('Self-healer: home area created:', boundaryName, '(boundary', boundaryId + ')',
          '— seeded with', boundaryIds.length, 'total boundaries');
        return { created: true, boundaryName, boundaryId };
      }
      // RPC found (or, on the exception-handler path, re-found) an existing home and stood down
      // — a correct outcome, not a failure. Matches the existing 'home-now-exists' contract
      // App.js/MyProfile.jsx already handle as success-equivalent.
      console.log('Self-healer: a home area already exists for this user — standing down.');
      return { created: false, reason: 'home-now-exists' };
    }

    if (!isAreaCodeCollision(rpcError)) {
      console.error('Self-healer: failed to create home area:', rpcError.message);
      return { created: false, reason: 'insert-error' };
    }

    lastError = rpcError;
    console.warn(`Self-healer: area_code collision on attempt ${attempt + 1}/${INSERT_RETRY_CAP} — retrying with a fresh code`);
  }

  // Retry cap exhausted on a collision alone (never any other error — those return above,
  // immediately). Astronomically unlikely: 3 independent draws from a 36^6 code space against at
  // most 10 existing codes per user. Still must surface something, per the prior brief — at
  // minimum a logged, visible signal, since an automatic caller may have no user to prompt.
  console.error('Self-healer: area_code collision exhausted all retries — home area NOT created', lastError?.message);
  return { created: false, reason: 'code-collision-exhausted' };
}
